// Trade-Off Optimization Agent - Maximize RPN Reduction Within Budget
// frontend/src/agents/specialized/TradeOffAgent.ts

import { BaseAgent } from '../core/BaseAgent';
import { AgentCommunicationHub } from '../core/AgentCommunicationHub';

export interface Optimization {
    id: string;
    name: string;
    description: string;
    target_fmea: string;  // Which FMEA item this addresses

    // Costs
    cost_delta: number;
    labor_hours?: number;

    // Benefits
    rpn_reduction: number;  // Negative number (e.g., -240)
    severity_change?: number;
    occurrence_change?: number;
    detection_change?: number;

    // Priority
    criticality: 'critical' | 'high' | 'medium' | 'low';
    target_severity: number;  // Original S rating
    target_rpn: number;  // Original RPN

    // Source
    source: 'research' | 'ai' | 'template' | 'evolution';
    confidence: 'high' | 'medium' | 'low';

    // Status
    selected?: boolean;
    rejection_reason?: string;
}

export interface OptimizationResult {
    selected: Optimization[];
    rejected: Optimization[];
    totalCost: number;
    totalRPNReduction: number;
    budgetUsed: number;
    budgetRemaining: number;
    meetsMinimumRPN: boolean;
}

export class TradeOffAgent extends BaseAgent {
    constructor(commHub: AgentCommunicationHub) {
        super('TradeOffAgent', commHub);
    }

    /**
     * Optimize optimizations to maximize RPN reduction within budget
     */
    async optimizeWithConstraints(
        optimizations: Optimization[],
        constraints: {
            budget: number;
            minRPNReduction: number;
            currentCost: number;
        }
    ): Promise<OptimizationResult> {

        console.log(`[TradeOffAgent] Optimizing ${optimizations.length} proposals...`);
        console.log(`[TradeOffAgent] Budget: $${constraints.budget.toFixed(2)}, Current: $${constraints.currentCost.toFixed(2)}, Available: $${(constraints.budget - constraints.currentCost).toFixed(2)}`);
        console.log(`[TradeOffAgent] Minimum RPN reduction required: ${constraints.minRPNReduction}`);

        const availableBudget = constraints.budget - constraints.currentCost;

        // Step 1: Calculate cost-effectiveness for each optimization
        const ranked = optimizations.map(opt => ({
            ...opt,
            rpn_per_dollar: Math.abs(opt.rpn_reduction) / Math.max(opt.cost_delta, 0.01),  // Avoid division by zero
            is_critical: opt.criticality === 'critical' || opt.target_rpn >= 125,
            is_free: opt.cost_delta <= 0  // Actually saves money!
        }));

        // Step 2: Sort by priority
        // Priority order:
        // 1. Free optimizations (cost <= 0)
        // 2. Critical items (regardless of cost-effectiveness)
        // 3. Best RPN per dollar
        ranked.sort((a, b) => {
            // Free optimizations first
            if (a.is_free && !b.is_free) return -1;
            if (!a.is_free && b.is_free) return 1;

            // Critical items second
            if (a.is_critical && !b.is_critical) return -1;
            if (!a.is_critical && b.is_critical) return 1;

            // Then by RPN per dollar
            return b.rpn_per_dollar - a.rpn_per_dollar;
        });

        console.log(`[TradeOffAgent] Ranked optimizations:`);
        for (const opt of ranked) {
            console.log(`  ${opt.is_critical ? '🔴' : opt.is_free ? '🟢' : '⚪'} ${opt.name}: $${opt.cost_delta.toFixed(2)}, RPN ${opt.rpn_reduction}, ${opt.rpn_per_dollar.toFixed(1)} RPN/$`);
        }

        // Step 3: Greedy knapsack selection
        const selected: Optimization[] = [];
        const rejected: Optimization[] = [];
        let costUsed = 0;
        let rpnReduction = 0;

        for (const opt of ranked) {
            const newCost = costUsed + opt.cost_delta;

            // Check if we can afford it
            if (newCost <= availableBudget) {
                // Can afford - select it
                selected.push({ ...opt, selected: true });
                costUsed += opt.cost_delta;
                rpnReduction += Math.abs(opt.rpn_reduction);

                console.log(`  ✅ Selected: ${opt.name} (Cost: $${opt.cost_delta.toFixed(2)}, RPN: ${opt.rpn_reduction})`);
            } else {
                // Cannot afford - reject it
                rejected.push({
                    ...opt,
                    selected: false,
                    rejection_reason: `Would exceed budget ($${newCost.toFixed(2)} > $${availableBudget.toFixed(2)})`
                });

                console.log(`  ❌ Rejected: ${opt.name} - ${rejected[rejected.length - 1].rejection_reason}`);
            }
        }

        // Step 4: Check if minimum RPN reduction met
        const meetsMinimumRPN = rpnReduction >= constraints.minRPNReduction;

        if (!meetsMinimumRPN) {
            console.warn(`[TradeOffAgent] ⚠️ WARNING: RPN reduction ${rpnReduction} < minimum ${constraints.minRPNReduction}`);

            // Try to find cheapest way to meet minimum
            const additional = await this.findCheapestToMeetMinimum(
                rejected,
                constraints.minRPNReduction - rpnReduction,
                availableBudget - costUsed
            );

            if (additional.length > 0) {
                console.log(`[TradeOffAgent] Found ${additional.length} additional items to meet minimum RPN`);
                for (const opt of additional) {
                    // Move from rejected to selected
                    const index = rejected.findIndex(r => r.id === opt.id);
                    if (index >= 0) {
                        rejected.splice(index, 1);
                    }
                    selected.push({ ...opt, selected: true });
                    costUsed += opt.cost_delta;
                    rpnReduction += Math.abs(opt.rpn_reduction);
                }
            }
        }

        // Step 5: Broadcast result
        this.broadcast({
            eventType: 'trade-off-optimization-complete',
            selected: selected.length,
            rejected: rejected.length,
            totalCost: costUsed,
            totalRPNReduction: rpnReduction,
            meetsMinimum: meetsMinimumRPN
        });

        console.log(`[TradeOffAgent] ✅ Optimization complete:`);
        console.log(`  Selected: ${selected.length} optimizations`);
        console.log(`  Rejected: ${rejected.length} optimizations`);
        console.log(`  Total cost: $${costUsed.toFixed(2)} / $${availableBudget.toFixed(2)}`);
        console.log(`  Total RPN reduction: ${rpnReduction}`);
        console.log(`  Meets minimum RPN: ${meetsMinimumRPN ? 'YES ✓' : 'NO ✗'}`);

        return {
            selected,
            rejected,
            totalCost: costUsed,
            totalRPNReduction: rpnReduction,
            budgetUsed: costUsed,
            budgetRemaining: availableBudget - costUsed,
            meetsMinimumRPN
        };
    }

    /**
     * Find cheapest combination to meet minimum RPN reduction
     */
    private async findCheapestToMeetMinimum(
        rejected: Optimization[],
        additionalRPNNeeded: number,
        budgetRemaining: number
    ): Promise<Optimization[]> {

        // Sort rejected by RPN (highest first)
        const sorted = [...rejected].sort((a, b) =>
            Math.abs(b.rpn_reduction) - Math.abs(a.rpn_reduction)
        );

        // Greedy: take highest RPN items until we meet minimum
        const additional: Optimization[] = [];
        let rpnGained = 0;
        let costUsed = 0;

        for (const opt of sorted) {
            if (rpnGained >= additionalRPNNeeded) {
                break;  // Met minimum
            }

            if (costUsed + opt.cost_delta <= budgetRemaining) {
                additional.push(opt);
                rpnGained += Math.abs(opt.rpn_reduction);
                costUsed += opt.cost_delta;
            }
        }

        // Check if we met the minimum
        if (rpnGained >= additionalRPNNeeded) {
            return additional;
        } else {
            // Couldn't meet minimum even with all remaining items
            console.warn(`[TradeOffAgent] Cannot meet minimum RPN reduction even with all rejected items`);
            return [];
        }
    }

    /**
     * Analyze trade-offs between two optimization sets
     */
    async compareScenarios(
        scenario1: Optimization[],
        scenario2: Optimization[],
        labels?: { scenario1: string; scenario2: string }
    ): Promise<{
        scenario1: { cost: number; rpn: number; count: number };
        scenario2: { cost: number; rpn: number; count: number };
        recommendation: string;
    }> {

        const calc = (opts: Optimization[]) => ({
            cost: opts.reduce((sum, o) => sum + o.cost_delta, 0),
            rpn: opts.reduce((sum, o) => sum + Math.abs(o.rpn_reduction), 0),
            count: opts.length
        });

        const s1 = calc(scenario1);
        const s2 = calc(scenario2);

        const s1_efficiency = s1.rpn / (s1.cost || 1);
        const s2_efficiency = s2.rpn / (s2.cost || 1);

        let recommendation = '';
        if (s1_efficiency > s2_efficiency) {
            recommendation = `${labels?.scenario1 || 'Scenario 1'} is more cost-effective (${s1_efficiency.toFixed(1)} RPN/$ vs ${s2_efficiency.toFixed(1)} RPN/$)`;
        } else {
            recommendation = `${labels?.scenario2 || 'Scenario 2'} is more cost-effective (${s2_efficiency.toFixed(1)} RPN/$ vs ${s1_efficiency.toFixed(1)} RPN/$)`;
        }

        return {
            scenario1: s1,
            scenario2: s2,
            recommendation
        };
    }

    /**
     * What-if analysis: What if we had more budget?
     */
    async whatIfMoreBudget(
        optimizations: Optimization[],
        currentBudget: number,
        additionalBudget: number
    ): Promise<{
        current: OptimizationResult;
        withMoreBudget: OptimizationResult;
        additionalRPNReduction: number;
    }> {

        const current = await this.optimizeWithConstraints(optimizations, {
            budget: currentBudget,
            minRPNReduction: 0,
            currentCost: 0
        });

        const withMore = await this.optimizeWithConstraints(optimizations, {
            budget: currentBudget + additionalBudget,
            minRPNReduction: 0,
            currentCost: 0
        });

        return {
            current,
            withMoreBudget: withMore,
            additionalRPNReduction: withMore.totalRPNReduction - current.totalRPNReduction
        };
    }
}
